import React, { ComponentProps } from "react";
import { Modal, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, ModalProps, Text } from "@chakra-ui/react";
import { colours } from "@/utils/themeUtils";

interface Props extends Omit<ModalProps, "isOpen" | "onClose" | "children"> {
	isOpen: boolean;
	onClose: () => void;
	title: string;
	/**
	 * This is expecting to take <ModalHeader>, <ModalBody> and/or <ModalFooter> as children.
	 */
	children: React.ReactNode;
	dataTestId?: string;
	closeButtonDataTestId?: string;
	minHeight?: string;
	maxHeight?: string;
	/** Overrides the max width set by `size` on the modal content panel. */
	maxW?: ComponentProps<typeof ModalContent>["maxW"];
}

/**
 * A standard implementation of the Chakra UI modal to promote consistency. Keeps all the modal styles in a single component to reduce repetition.
 */
export function ClasswizeModal(props: Props): JSX.Element {
	const { isOpen, onClose, title, children, dataTestId, closeButtonDataTestId, minHeight, maxHeight, maxW, ...modalProps } = props;

	return (
		<Modal data-testid={dataTestId} isOpen={isOpen} onClose={onClose} isCentered {...modalProps}>
			<ModalOverlay />
			<ModalContent data-testid={`${dataTestId}-content`} p={3} minHeight={minHeight} maxHeight={maxHeight} maxW={maxW}>
				<ModalHeader>
					<Text fontSize={"20px"} color={colours.text.body} fontWeight={500} fontFamily={"Poppins"}>
						{title}
					</Text>
				</ModalHeader>
				<ModalCloseButton mt={2} mr={1} size={"lg"} data-testid={closeButtonDataTestId ?? "modal-close-btn"} />
				{/* children is expected to be <ModalHeader>, <ModalBody> and/or <ModalFooter> */}
				{children}
			</ModalContent>
		</Modal>
	);
}
